import React, { FormEvent, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { TwoColumnLayout } from '../../pages/BasePage';
import { UEventFormField, useEditEventContext } from '../../contexts/EditEventContext';
import EventFormInput, { InputType } from '../EventForm/EventFormInput';
import DateFormInput from '../EventForm/DateFormInput';
import TimeFormInput from '../EventForm/TimeFormInput';
import { convertTimestampToDate, convertTimestampToTimeString, convertUserTime } from '../../lib/time';
import GeneratedImageSelection from '../../pages/Create/GeneratedImageSelection';
import { getPalette } from '../../lib/colorPicker';
import { getPhotoDataUrl } from '../../lib/images';
import ExpandingTextInput from '../ExpandingTextInput';
import { IUEvent } from '../../lib/event';
import Questions from '../questions/Questions';
import { useModalContext } from '../../contexts/ModalContext';
import CheckboxInputRow from '../EventForm/CheckboxInputRow';
import { validEventLocation } from '../../lib/location';
import { useUserContext } from '../../contexts/UserContext';

import AddressAutocomplete from 'Common/src/components/AddressAutocomplete/AddressAutocomplete';
import { calculateTimeZone } from 'Common/src/api/GoogleTimezoneApi';

import { TAppAddress } from 'TProtocol/common/models';
import { TAppElkLocation } from 'TProtocol/prototypes/events/messages';
import { Container } from './Input';

const EventForm = ({ event, formRef }: { event: IUEvent, formRef: RefObject<HTMLFormElement> }) => {
  const userContext = useUserContext();
  const editEventContext = useEditEventContext();
  const modalContext = useModalContext();
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [isChecked, setIsChecked] = useState<boolean>(event.openInvite ?? true);
  const [isGuestListChecked, setIsGuestListChecked] = useState<boolean>(event.publicGuestList ?? true);
  const [areTextRemindersEnabled, setAreTextRemindersEnabled] = useState<boolean>(event.remindersEnabled ?? true);
  const [day, setDay] = useState<Date | null>(
    event.date ? event.date : (event.startTime ? convertTimestampToDate(event.startTime, event.timeZone) : null)
  );
  const [startTime, setStartTime] = useState<string>(
    event.date ? '' : (event.startTime ? convertTimestampToTimeString(event.startTime, event.timeZone) : '' || '')
  );
  const [endTime, setEndTime] = useState<string>(
    event.date ? '' : (event.endTime ? convertTimestampToTimeString(event.endTime, event.timeZone) || '' : '')
  );
  const [timeZone, setTimeZone] = useState<string>(
    event.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const firstUpdate = useRef(!event.isFromInspiration);

  useEffect(() => {
    if (day !== null && startTime !== '') {
      const startDatetime = convertUserTime(day, startTime, timeZone);
      const endDatetime = convertUserTime(day, endTime, timeZone, true);

      if (startDatetime !== null) {
        editEventContext.setTimes(startDatetime, endDatetime ?? 0);
        return;
      }
    }
  }, [day, startTime, endTime]);

  useEffect(() => {
    if (!firstUpdate.current) {
      void calculatePalette();
    }
    firstUpdate.current = false;
  }, [event.photoUrl]);

  const calculatePalette = async () => {
    if (event.photoUrl && event.photoUrl !== editEventContext.previousEvent?.photoUrl) {
      const dataUrl = await getPhotoDataUrl(event.photoUrl);
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = dataUrl;

      img.onload = () => {
        const colors = getPalette(img);
        if (colors) {
          editEventContext.setColors(colors);
        }
      };
    }
  };

  const handleInputChange = (name: string, value: string) => {
    if (name === 'description') {
      editEventContext.setDescription(value);
    } else if (name === 'hosted') {
      editEventContext.setHostedBy(value);
    } else if (name === 'hostEmail') {
      editEventContext.setHostEmail(value);
    } else if (name === 'title') {
      editEventContext.setTitle(value);
    } else if (name === 'subtitle') {
      editEventContext.setSubtitle(value);
    }
  };

  const handleAddressChange = (addressStr: string, address?: TAppAddress) => {
    editEventContext.setLocation(new TAppElkLocation({ displayName: addressStr, address: address }));
    editEventContext.updateFormErrors({
      ...event.errors,
      invalidLocation: false
    });
  };

  const onDayChange = (date: Date) => {
    setDay(date);
    updateDate(date, startTime, endTime, timeZone);
  };

  const onStartTimeChange = (start: string) => {
    setStartTime(start);
    updateDate(day, start, endTime, timeZone);
  };

  const onEndTimeChange = (end: string) => {
    setEndTime(end);
    updateDate(day, startTime, end, timeZone);
  };

  const handleLatLonChange = async (lat: number, lng: number) => {
    const timeZone = await calculateTimeZone(lat, lng, event?.startTime);
    setTimeZone(timeZone);
    editEventContext.setTimeZone(timeZone);
    updateDate(day, startTime, endTime, timeZone);
  };

  const updateDate = (day: Date | null, startTimeStr: string, endTimeStr: string, timeZone: string) => {
    if (day !== null && startTimeStr !== '') {
      const startTime = convertUserTime(day, startTimeStr, timeZone);
      const endTime: number | null = endTimeStr !== '' ? convertUserTime(day, endTimeStr, timeZone, true) : null;

      const isValid = startTime !== null && (!endTimeStr || (endTime !== null && endTime > startTime));
      if (isValid) {
        editEventContext.setTimes(startTime, endTime ?? undefined);
        return;
      }
    }
  };

  const imageContent = <GeneratedImageSelection event={event}/>;

  const onCheckboxChange = (e: FormEvent<HTMLInputElement>) => {
    const isChecked = e.currentTarget.checked;
    setIsChecked(isChecked);
    editEventContext.setIsOpenInvite(isChecked);
  };
  const onGuestListCheckboxChange = (e: FormEvent<HTMLInputElement>) => {
    const isGuestListCheckedLocal = e.currentTarget.checked;
    setIsGuestListChecked(isGuestListCheckedLocal);
    editEventContext.setShowGuestList(isGuestListCheckedLocal);
  };
  const onReminderCheckboxChange = (e: FormEvent<HTMLInputElement>) => {
    const isReminderCheckedLocal = e.currentTarget.checked;
    setAreTextRemindersEnabled(isReminderCheckedLocal);
    editEventContext.setRemindersEnabled(isReminderCheckedLocal);
  };

  const onInvalidLocation = () => {
    editEventContext.updateFormErrors({
      ...event.errors,
      invalidLocation: true
    });
  };

  return <form ref={formRef}>
    <ImageContainer>
      {imageContent}
    </ImageContainer>

    <DetailsSectionText>Details</DetailsSectionText>

    <TwoColumnLayout $gap={30}>
      <Column>
        <EventFormColumn>
          <EventFormInput
            placeholder="-"
            event={event}
            label="Hosted by?"
            value={event.hostedBy}
            onChange={(value) => handleInputChange('hosted', value)}
          />

          {event.isCreate ? <EventFormInput
            placeholder="-"
            event={event}
            label="Host Email"
            value={event.hostEmail ?? ''}
            onChange={(value) => handleInputChange('hostEmail', value)}
            autoComplete="email"
          /> : null}

          <div>
            <DateFormInput
              label="When?"
              value={day}
              onChange={onDayChange}
              required={true}
              onInvalid={() => editEventContext.setFieldInvalid(UEventFormField.Day)}
              errorRef={(div: HTMLDivElement) => editEventContext.setFormErrorDiv(UEventFormField.Day, div)}
            />
          </div>

          <TimeFormInput
            startTime={startTime}
            endTime={endTime}
            onStartChange={onStartTimeChange}
            onEndChange={onEndTimeChange}
            onInvalid={() => editEventContext.setFieldInvalid(UEventFormField.Time)}
            error={event.errors.invalidDatePair ? 'End time must be after start time' : undefined}
            required={true}
            errorRef={(div: HTMLDivElement) => editEventContext.setFormErrorDiv(UEventFormField.Time, div)}
          />

          <AddressAutocomplete
            inputRef={addressInputRef}
            onSelect={handleAddressChange}
            onSelectLatLon={handleLatLonChange}
            validator={validEventLocation}
            onInvalid={onInvalidLocation}>
            <EventFormInput
              event={event}
              label="Where?"
              value={event.location.displayName}
              onChange={handleAddressChange}
              error={event.errors.invalidLocation ? 'Select a valid location' : undefined}
              required={true}
              inputRef={addressInputRef}
              onInvalid={() => editEventContext.setFieldInvalid(UEventFormField.Location)}
              errorRef={(div: HTMLDivElement) => editEventContext.setFormErrorDiv(UEventFormField.Location, div)}
              placeholder=""
            />
          </AddressAutocomplete>
        </EventFormColumn>
      </Column>
      <Column>
        <EventFormColumn>
          <ExpandingTextInput
            label="Description"
            value={event.description}
            charLimit={1000}
            onChange={(value) => handleInputChange('description', value)}
            textColor="var(--shine-text-color)"
          />

          <Questions event={event}/>

          <CheckboxesContainer>
            <CheckboxInputRow id="field-add"
                              disabled={false}
                              checked={isChecked}
                              onChange={onCheckboxChange}
                              label="Allow guests to invite more people"/>
            <CheckboxInputRow id="GuestList"
                              disabled={false}
                              checked={isGuestListChecked}
                              onChange={onGuestListCheckboxChange}
                              label="Display guest list"/>
            <CheckboxInputRow id="Reminders"
                              disabled={false}
                              checked={areTextRemindersEnabled}
                              onChange={onReminderCheckboxChange}
                              label="Send guests automatic reminders"/>
          </CheckboxesContainer>
        </EventFormColumn>
      </Column>
    </TwoColumnLayout>
  </form>;
};

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 550px;
  max-width: 100%;
`;

const ImageContainer = styled.div`
  text-align: center;
  margin: 15px 0;
`;

export const ImageAction = styled.span`
  font-size: 18px;
  font-weight: 300;
  cursor: pointer;
`;

export const ErrorContainer = styled.div`
  font-size: 16px;
  color: var(--shine-system-error-color);
  font-weight: bold;
  margin: 1px 0;
  text-align: left;
`;

export const PalettePickerContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  padding: 5px 15px;
  margin-top: 20px;
`;

export const EventFormColumn = styled.div`
  ${Container}:first-child {
    margin-top: 0;
  }
`;

const CheckboxesContainer = styled.div`
  color: var(--shine-background-text-color);
`;

const DetailsSectionText = styled.div`
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 15px;
  color: var(--shine-background-text-color);
`

export default EventForm;

